import { Stack } from 'expo-router';
import { Text, YStack } from 'tamagui';

import { Image } from 'expo-image';

import { PSHeaderBackButton } from '@/components/PSHeaderButtons';
import PSLinkButton from './ui/PSLinkButton';

/**
 * EventErrorPage — what an event's routes draw when the event fetch fails.
 *
 * The `error` branch of `useEventCart().eventState` (see `app/event/[id]`). The
 * event never arrived, so there is no page to draw AROUND the failure the way
 * `PSHomeSectionError` sits inside a home section — the failure IS the screen:
 * the message centred on the page, with the one control that can change the
 * situation under it.
 *
 * ── Header ──
 * Back only. The share circle the loaded screen carries has nothing to share,
 * and the same is true of every other affordance a real event page has — so this
 * takes the identical two-path transparent header (see `PSHeaderBackButton` and
 * the long note in `app/event/[id]/index.tsx`) minus the right-hand item. Leaving
 * the header on matters: a dead-end screen the user can't back out of is worse
 * than the error itself.
 *
 * ── Props ──
 * It's handed the WORDS, not the `ApiError`. Every kind's `message` is already
 * user-facing by the time it reaches a view (`apiError.ts` swaps a server's 5xx
 * text for generic copy and a dead connection for actionable copy, and only
 * keeps the server's own sentence for a 4xx) — so unlike `PSHomeSectionError`,
 * which picks a sentence per kind because home also decides on ART per kind,
 * there's nothing left for this component to look up.
 *
 * ── Root, not PageScrollView ──
 * A bare `flex={1}` stack, like `FullScreenMessage` in the confirm screen:
 * PageScrollView's ScrollView sizes its content container to its children, so
 * a `flex={1}` centering child inside it never gets a height to center
 * within — the art ends up pinned under the header instead of mid-screen.
 */
export default function EventErrorPage({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <YStack
      flex={1}
      items="center"
      justify="center"
      gap="$space.xl"
      bg="$color.background"
    >
      <Stack.Screen
        options={{
          headerTransparent: true,
          headerTitle: () => <></>,
          headerLeft: () => <PSHeaderBackButton />,
          unstable_headerLeftItems: () => [
            {
              type: 'custom',
              element: <PSHeaderBackButton />,
              hidesSharedBackground: true,
            },
          ],
        }}
      />

      <Image
        source={require('@/assets/images/no-internet-cat.png')}
        style={{
          // width: ART_WIDTH,
          // height: ART_WIDTH / ART_RATIO,
          width: 400,
          height: 200,
          alignSelf: 'center',
        }}
        contentFit="contain"
        transition={200}
      />
      <Text
        fontFamily="$body"
        fontSize="$base"
        color="$textDark"
        textAlign="center"
      >
        {message}
      </Text>
      <PSLinkButton title="Retry" onPress={onRetry} />
    </YStack>
  );
}
